import React, { useEffect, useMemo, useReducer, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Inputs, DatePickerComponent } from '../../../../../../Components';
import LookupAutocomplete from '../../../../../../Components/Controls/LookupAutocomplete/LookupAutocomplete';
import { ButtonBase, DialogActions } from '@material-ui/core';
import {
  CreateAgreementDocumentAPI,
  CreateVendorAgreementAPI,
  DeleteAgreementDocumentAPI,
  GetAllAgreementDocumentsAPI,
  UpdateVendorAgreementAPI,
} from '../../../../../../Services';
import { GetParams, showError, showSuccess } from '../../../../../../Helper';
import { FooterWrapperComponent } from '../../../VendorsUtilities';
import { useKeyGenerator } from '../../../../../../Hooks/keyGenerator';
import { ActivityFilesUploader } from '../../../../../../Components/UploaderActivitieFileComponent/ActivityFilesUploader';
import { isEmpty } from 'lodash';

const reducer = (state, action) => {
  if (action.id === 'edit') {
    return {
      ...action.value,
    };
  } else if (action.id === 'edit-some') {
    return {
      ...state,
      ...action.value,
    };
  } else if (action.id) {
    return { ...state, [action.id]: action.value };
  }
  return state;
};
export const AgreementDetailsManagementComponents = ({
  parentTranslationPath,
  className,
  reloadData,
  activeItem,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation(parentTranslationPath);
  const [key, refresh] = useKeyGenerator();

  const [isLoading, setIsLoading] = useState(false);
  const defaultState = useMemo(
    () => ({
      vendorId: +GetParams('id'),
      contractStartDate: null,
      contractDuration: null,
      contractExpiryDate: null,
      agreementStatusId: null,
      psiCommission: null,
      remarks: '',
      agreementsStatus: null,
      files: [],
    }),
    []
  );
  const [state, dispatch] = useReducer(
    reducer,
    activeItem
      ? {
          ...activeItem,
          agreementsStatus: {
            lookupItemId: activeItem.agreementStatus?.agreementStatusId,
            lookupItemName: activeItem.agreementStatus?.agreementStatusName,
          },
          files: activeItem.documents.map((file) => ({
            ...file,
            uuid: file.fileId,
          })),
          vendorId: +GetParams('id'),
        }
      : {
          ...defaultState,
        }
  );
  const isValidDate = (date) => moment(date).isValid();

  const clickHandler = async () => {
    try {
      setIsLoading(true);
      if (!state.contractExpiryDate || !state.contractStartDate) {
        showError(t(`fill_agreement_start_date_and_end_date`));
        return;
      }
      state.agreementStatusId = state.agreementsStatus?.lookupItemId;
      const { agreementsStatus, files, ...body } = state;
      body.documents = state.files?.map((file) => ({
        fileId: file.uuid,
        fileName: file.fileName,
      }));
      if (activeItem) {
        await UpdateVendorAgreementAPI(activeItem.vendorAgreementId, body);
        showSuccess(t`agreement_updated_successfully`);
        onSave();
      } else {
        await CreateVendorAgreementAPI(body);
        showSuccess(t`agreement_created_successfully`);
        reloadData();
      }
      dispatch({ value: defaultState, id: 'edit' });
      refresh();
    } catch (error) {
      showError(t`something_went_wrong`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div key={key}>
      <div className={`${className || 'px-2 w-50 '}`}>
        <LookupAutocomplete
          lookupName={'VendorAgreementStatus'}
          selectedValues={state.agreementsStatus}
          placeholder={t('select-agreement-status')}
          inputClasses='inputs theme-form-builder'
          label={'agreement-status'}
          parentTranslationPath={parentTranslationPath}
          onChange={(_, newValue) => {
            dispatch({ id: 'agreementsStatus', value: newValue });
          }}
        />
      </div>

      <div className={`${className || 'px-2 w-50 '}`}>
        <DatePickerComponent
          idRef={'agreementStartDateRef'}
          labelValue={'agreement-start-date'}
          value={
            isValidDate(state.contractStartDate) &&
            moment(state.contractStartDate).format('YYYY-MM-DDTHH:mm:ss')
          }
          onDateChanged={(newValue) => {
            if (state.contractExpiryDate) {
              dispatch({
                id: 'edit-some',
                value: {
                  contractStartDate: moment(newValue).format(
                    'YYYY-MM-DDTHH:mm:ss'
                  ),
                  contractDuration: moment(state.contractExpiryDate).diff(
                    moment(newValue),
                    'months'
                  ),
                },
              });
            } else {
              dispatch({
                id: 'contractStartDate',
                value: moment(newValue).format('YYYY-MM-DDTHH:mm:ss'),
              });
            }
          }}
          maxDate={
            isValidDate(state.contractExpiryDate) &&
            moment(state.contractExpiryDate).format('YYYY-MM-DDTHH:mm:ss')
          }
          placeholder={'select-agreement-start-date'}
          parentTranslationPath={parentTranslationPath}
        />
      </div>
      <div className={`${className || 'px-2 w-50 '}`}>
        <Inputs
          isAttachedInput
          idRef={`agreementDurationMonthsIDRef`}
          labelValue={'agreement-duration-months'}
          value={state.contractDuration}
          type={'number'}
          min={0}
          onInputChanged={(e) => {
            const { value } = e.target;
            dispatch({
              id: 'edit-some',
              value: {
                contractExpiryDate: moment(state.contractStartDate).add(
                  +value,
                  'months'
                ),
                contractDuration: +value,
              },
            });
          }}
          inputPlaceholder={'enter-agreement-duration-months'}
          parentTranslationPath={parentTranslationPath}
          isDisabled={!state.contractStartDate}
        />
      </div>
      <div className={`${className || 'px-2 w-50 '}`}>
        <DatePickerComponent
          idRef={'AgreementEndDateRef'}
          value={
            isValidDate(state.contractExpiryDate) &&
            moment(state.contractExpiryDate).format('YYYY-MM-DDTHH:mm:ss')
          }
          labelValue={'agreement-end-date'}
          placeholder={'select-agreement-end-date'}
          onDateChanged={(newValue) => {
            dispatch({
              id: 'edit-some',
              value: {
                contractExpiryDate: moment(newValue).format(
                  'YYYY-MM-DDTHH:mm:ss'
                ),
                contractDuration: moment(newValue).diff(
                  moment(state.contractStartDate),
                  'months'
                ),
              },
            });
          }}
          parentTranslationPath={parentTranslationPath}
          minDate={moment(state.contractStartDate)}
          isDisabled={!state.contractStartDate}
        />
      </div>
      <div className={`${className || 'px-2 w-50 '}`}>
        <Inputs
          type={'number'}
          endAdornment={<span className='text-muted text-center p-1'>%</span>}
          labelValue={'commission'}
          wrapperClasses={'theme-form-builder'}
          parentTranslationPath={parentTranslationPath}
          step={0.1}
          value={state.psiCommission}
          inputPlaceholder={'psi_commission'}
          onInputChanged={(e) => {
            dispatch({ id: 'psiCommission', value: e.target.valueAsNumber });
          }}
        />
      </div>
      <div className={`${className || 'px-2 w-50 '}`}>
        <Inputs
          multiline
          rows={4}
          labelValue={'remarks'}
          wrapperClasses={'theme-form-builder'}
          parentTranslationPath={parentTranslationPath}
          step={0.1}
          inputPlaceholder={'remarks'}
          value={state.remarks}
          onInputChanged={(e) => {
            dispatch({ id: 'remarks', value: e.target.value });
          }}
        />
      </div>
      <div className='py-3'>
        <ActivityFilesUploader
          multiple
          accept={
            'application/pdf, image/png, image/jpg, application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          }
          label={t('upload_agreement_document')}
          state={state}
          setState={dispatch}
          // isDisabled={!isEmpty(state.files)}
          // flagAsDeleted={!!activeItem}
          showLoading
        />
      </div>
      {!activeItem ? (
        <div className='mt-3 d-flex-v-center-h-end flex-wrap'>
          <ButtonBase
            className='btns theme-solid'
            onClick={clickHandler}
            disabled={isLoading || !state.agreementsStatus?.lookupItemId}
          >
            <span className='mdi mdi-plus' />
            {t(`add-agreement-details`)}
          </ButtonBase>
        </div>
      ) : (
        <DialogActions>
          <FooterWrapperComponent
            parentTranslationPath={parentTranslationPath}
            onClose={onClose}
            onSave={clickHandler}
            disabled={isLoading}
          />
        </DialogActions>
      )}
    </div>
  );
};
AgreementDetailsManagementComponents.propTypes = {
  parentTranslationPath: PropTypes.string.isRequired,
  state: PropTypes.objectOf(PropTypes.any).isRequired,
  setState: PropTypes.func.isRequired,
  selected: PropTypes.objectOf(PropTypes.any).isRequired,
  setSelected: PropTypes.func.isRequired,
  className: PropTypes.string,
  editForm: PropTypes.bool,
};

AgreementDetailsManagementComponents.defaultProps = {
  editForm: false,
  className: '',
};
